import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  messageService,
  UserInquiry,
  MessageFilters,
} from "../../services/messageService";
import { useAuth } from "../../contexts/AuthContext";

const ManagementContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: var(--text-color);
  margin: 0;
`;

const ViewControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ViewToggle = styled.div`
  display: flex;
  background: var(--background);
  border-radius: 8px;
  padding: 4px;
  border: 1px solid var(--border-color);
`;

const ViewButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  border: none;
  background: ${(props) =>
    props.$active ? "var(--primary-color)" : "transparent"};
  color: ${(props) => (props.$active ? "white" : "var(--text-color)")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$active ? "var(--primary-color)" : "var(--card-background)"};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const FiltersContainer = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid var(--border-color);
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FilterInput = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background);
  color: var(--text-color);
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background);
  color: var(--text-color);
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const InquiriesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InquiryCard = styled.div<{ $status: string; $isCompact?: boolean }>`
  background: var(--card-background);
  border-radius: 12px;
  padding: ${(props) => (props.$isCompact ? "1rem" : "1.5rem")};
  border: 1px solid var(--border-color);
  border-left: 4px solid
    ${(props) =>
      props.$status === "pending"
        ? "#f59e0b"
        : props.$status === "resolved"
        ? "#10b981"
        : props.$status === "reviewed"
        ? "#3b82f6"
        : "#6b7280"};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const InquiryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const InquiryMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) =>
    props.$status === "pending"
      ? "#fef3c7"
      : props.$status === "resolved"
      ? "#d1fae5"
      : props.$status === "reviewed"
      ? "#dbeafe"
      : "#f3f4f6"};
  color: ${(props) =>
    props.$status === "pending"
      ? "#92400e"
      : props.$status === "resolved"
      ? "#065f46"
      : props.$status === "reviewed"
      ? "#1e40af"
      : "#374151"};
`;

const MessageContent = styled.div`
  margin-bottom: 1rem;
`;

const MessageText = styled.div`
  background: var(--background);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  font-family: "Courier New", monospace;
  font-size: 0.9rem;
  white-space: pre-wrap;
`;

const ResponseText = styled.div`
  background: #f0f9ff;
  padding: 1rem;
  border-radius: 8px;
  border-left: 3px solid #0ea5e9;
  font-size: 0.9rem;
  white-space: pre-wrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" | "danger" }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.$variant === "primary" &&
    `
    background: var(--primary-color);
    color: white;
    
    &:hover {
      background: var(--primary-dark);
    }
  `}

  ${(props) =>
    props.$variant === "secondary" &&
    `
    background: var(--background);
    color: var(--text-color);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--card-background);
    }
  `}

  ${(props) =>
    props.$variant === "danger" &&
    `
    background: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
    }
  `}
`;

const AdminNotes = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background);
  color: var(--text-color);
  font-size: 0.9rem;
  margin-top: 0.5rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const CompactInquiryCard = styled.div<{ $status: string }>`
  background: var(--card-background);
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-left: 4px solid
    ${(props) =>
      props.$status === "pending"
        ? "#f59e0b"
        : props.$status === "resolved"
        ? "#10b981"
        : props.$status === "reviewed"
        ? "#3b82f6"
        : "#6b7280"};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CompactHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CompactMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`;

const UserEmail = styled.div`
  background: #f0f9ff;
  color: #0c4a6e;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.75rem;
  border: 1px solid #0ea5e9;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e0f2fe;
    transform: translateY(-1px);
  }
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: #0c4a6e;
  cursor: pointer;
  padding: 0;
  font-size: 0.7rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const CompactMessage = styled.div`
  font-size: 0.9rem;
  color: var(--text-color);
  margin-bottom: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompactResponse = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
  background: var(--background);
  padding: 0.5rem;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InquiryManagement: React.FC = () => {
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState<UserInquiry[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<MessageFilters>({ status: "pending" });
  const [editingInquiry, setEditingInquiry] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<string>("");
  const [adminResponse, setAdminResponse] = useState<string>("");
  const [viewMode, setViewMode] = useState<"compact" | "detailed">("compact");
  const [expandedInquiry, setExpandedInquiry] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  const loadInquiries = async (currentFilters: MessageFilters) => {
    try {
      setLoading(true);
      const data = await messageService.getInquiries(currentFilters);
      setInquiries(data);
    } catch (error) {
      console.error("Error loading inquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await messageService.getInquiryStats();
      setStats(data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  // Load inquiries when filters change
  useEffect(() => {
    loadInquiries(filters);
  }, [filters]);

  // Load stats on component mount
  useEffect(() => {
    loadStats();
  }, []);

  const handleStatusChange = async (inquiryId: string, status: string) => {
    try {
      await messageService.updateInquiry(inquiryId, { status: status as any });
      await loadInquiries(filters);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleSaveNotes = async (inquiryId: string) => {
    try {
      await messageService.updateInquiry(inquiryId, {
        adminNotes,
        adminRespondedBy: user?.id,
      });
      setEditingInquiry(null);
      setAdminNotes("");
      await loadInquiries(filters);
    } catch (error) {
      console.error("Error saving notes:", error);
    }
  };

  const handleResolve = async (inquiryId: string) => {
    try {
      await messageService.resolveInquiry(
        inquiryId,
        adminResponse,
        user?.id || "unknown",
        adminNotes
      );
      setEditingInquiry(null);
      setAdminResponse("");
      setAdminNotes("");
      await loadInquiries(filters);
    } catch (error) {
      console.error("Error resolving inquiry:", error);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "Unknown";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const copyEmailToClipboard = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (error) {
      console.error("Failed to copy email:", error);
    }
  };

  if (loading) {
    return <div>Loading inquiries...</div>;
  }

  return (
    <ManagementContainer>
      <Header>
        <Title>Inquiry Management</Title>
        <ViewControls>
          <ViewToggle>
            <ViewButton
              $active={viewMode === "compact"}
              onClick={() => setViewMode("compact")}
            >
              Compact View
            </ViewButton>
            <ViewButton
              $active={viewMode === "detailed"}
              onClick={() => setViewMode("detailed")}
            >
              Detailed View
            </ViewButton>
          </ViewToggle>
          <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)" }}>
            {inquiries.length} inquiries
          </div>
        </ViewControls>
      </Header>

      {stats && (
        <StatsGrid>
          <StatCard>
            <StatNumber>{stats.total}</StatNumber>
            <StatLabel>Total Inquiries</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.pending}</StatNumber>
            <StatLabel>Pending Review</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.fallbackCount}</StatNumber>
            <StatLabel>Fallback Responses</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.dialogflowCount}</StatNumber>
            <StatLabel>Dialogflow Responses</StatLabel>
          </StatCard>
        </StatsGrid>
      )}

      <FiltersContainer>
        <FilterRow>
          <FilterSelect
            value={filters.status || ""}
            onChange={(e) => {
              const newStatus = e.target.value || undefined;
              setFilters((prev) => ({ ...prev, status: newStatus }));
            }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
            <option value="archived">Archived</option>
          </FilterSelect>

          <FilterSelect
            value={filters.intent || ""}
            onChange={(e) => {
              const newIntent = e.target.value || undefined;
              setFilters((prev) => ({ ...prev, intent: newIntent }));
            }}
          >
            <option value="">All Intents</option>
            <option value="ReportBug">Bug Reports</option>
            <option value="RequestFeature">Feature Requests</option>
            <option value="GeneralQuestion">General Questions</option>
            <option value="AccountHelp">Account Help</option>
            <option value="TechnicalSupport">Technical Support</option>
          </FilterSelect>

          <FilterSelect
            value={
              filters.isDialogflowResponse === undefined
                ? ""
                : filters.isDialogflowResponse.toString()
            }
            onChange={(e) => {
              const newDialogflowResponse =
                e.target.value === "" ? undefined : e.target.value === "true";
              setFilters((prev) => ({
                ...prev,
                isDialogflowResponse: newDialogflowResponse,
              }));
            }}
          >
            <option value="">All Responses</option>
            <option value="true">Dialogflow</option>
            <option value="false">Fallback</option>
          </FilterSelect>

          <FilterInput
            type="text"
            placeholder="Search messages..."
            value={filters.searchTerm || ""}
            onChange={(e) => {
              const newSearchTerm = e.target.value || undefined;
              setFilters((prev) => ({
                ...prev,
                searchTerm: newSearchTerm,
              }));
            }}
          />
        </FilterRow>
      </FiltersContainer>

      <InquiriesList>
        {inquiries.map((inquiry) => {
          const isExpanded = expandedInquiry === inquiry.id;
          const isEditing = editingInquiry === inquiry.id;

          if (viewMode === "compact") {
            return (
              <CompactInquiryCard
                key={inquiry.id}
                $status={inquiry.status}
                onClick={() =>
                  setExpandedInquiry(isExpanded ? null : inquiry.id!)
                }
              >
                <CompactHeader>
                  <div>
                    <strong>#{inquiry.id?.slice(-8)}</strong>
                    <StatusBadge $status={inquiry.status}>
                      {inquiry.status}
                    </StatusBadge>
                  </div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {formatDate(inquiry.timestamp)}
                  </div>
                </CompactHeader>

                <CompactMeta>
                  <span>Intent: {inquiry.intent}</span>
                  <span>
                    Confidence: {(inquiry.confidence * 100).toFixed(1)}%
                  </span>
                  <span>
                    Source:{" "}
                    {inquiry.isDialogflowResponse ? "Dialogflow" : "Fallback"}
                  </span>
                  {inquiry.userEmail && (
                    <UserEmail
                      onClick={() => copyEmailToClipboard(inquiry.userEmail!)}
                    >
                      📧 {inquiry.userEmail}
                      <CopyButton title="Copy email">
                        {copiedEmail === inquiry.userEmail ? "✓" : "📋"}
                      </CopyButton>
                    </UserEmail>
                  )}
                </CompactMeta>

                <CompactMessage>
                  <strong>User:</strong> {inquiry.message}
                </CompactMessage>

                <CompactResponse>
                  <strong>AI:</strong> {inquiry.response}
                </CompactResponse>

                {isExpanded && (
                  <div
                    style={{
                      marginTop: "1rem",
                      paddingTop: "1rem",
                      borderTop: "1px solid var(--border-color)",
                    }}
                  >
                    <div style={{ marginBottom: "1rem" }}>
                      <strong>Full Conversation:</strong>
                      <div
                        style={{
                          background: "var(--background)",
                          padding: "0.5rem",
                          borderRadius: "6px",
                          marginTop: "0.5rem",
                        }}
                      >
                        <div>
                          <strong>User:</strong> {inquiry.message}
                        </div>
                        <div style={{ marginTop: "0.5rem" }}>
                          <strong>AI:</strong> {inquiry.response}
                        </div>
                      </div>
                    </div>

                    {inquiry.adminNotes && (
                      <div style={{ marginBottom: "1rem" }}>
                        <strong>Admin Notes:</strong>
                        <div
                          style={{
                            background: "var(--background)",
                            padding: "0.5rem",
                            borderRadius: "6px",
                            marginTop: "0.5rem",
                          }}
                        >
                          {inquiry.adminNotes}
                        </div>
                      </div>
                    )}

                    {inquiry.adminResponse && (
                      <div style={{ marginBottom: "1rem" }}>
                        <strong>Admin Response:</strong>
                        <div
                          style={{
                            background: "var(--background)",
                            padding: "0.5rem",
                            borderRadius: "6px",
                            marginTop: "0.5rem",
                          }}
                        >
                          {inquiry.adminResponse}
                        </div>
                      </div>
                    )}

                    <ActionButtons>
                      <Button
                        $variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusChange(inquiry.id!, "reviewed");
                        }}
                        disabled={inquiry.status === "reviewed"}
                      >
                        Mark Reviewed
                      </Button>

                      <Button
                        $variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingInquiry(inquiry.id!);
                        }}
                      >
                        Add Notes
                      </Button>

                      <Button
                        $variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusChange(inquiry.id!, "resolved");
                        }}
                        disabled={inquiry.status === "resolved"}
                      >
                        Mark Resolved
                      </Button>

                      <Button
                        $variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusChange(inquiry.id!, "archived");
                        }}
                        disabled={inquiry.status === "archived"}
                      >
                        Archive
                      </Button>
                    </ActionButtons>

                    {isEditing && (
                      <div
                        style={{
                          marginTop: "1rem",
                          paddingTop: "1rem",
                          borderTop: "1px solid var(--border-color)",
                        }}
                      >
                        <div>
                          <strong>Add Admin Notes:</strong>
                        </div>
                        <AdminNotes
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder="Add your notes here..."
                        />

                        <div style={{ marginTop: "0.5rem" }}>
                          <strong>Admin Response (optional):</strong>
                          <AdminNotes
                            value={adminResponse}
                            onChange={(e) => setAdminResponse(e.target.value)}
                            placeholder="Add a response to the user..."
                            style={{ minHeight: "60px" }}
                          />
                        </div>

                        <ActionButtons style={{ marginTop: "0.5rem" }}>
                          <Button
                            $variant="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveNotes(inquiry.id!);
                            }}
                          >
                            Save Notes
                          </Button>

                          <Button
                            $variant="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResolve(inquiry.id!);
                            }}
                          >
                            Resolve with Response
                          </Button>

                          <Button
                            $variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(inquiry.id!, "archived");
                            }}
                            disabled={inquiry.status === "archived"}
                          >
                            Archive
                          </Button>

                          <Button
                            $variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingInquiry(null);
                              setAdminNotes("");
                              setAdminResponse("");
                            }}
                          >
                            Cancel
                          </Button>
                        </ActionButtons>
                      </div>
                    )}
                  </div>
                )}
              </CompactInquiryCard>
            );
          }

          return (
            <InquiryCard key={inquiry.id} $status={inquiry.status}>
              <InquiryHeader>
                <div>
                  <h3>Inquiry #{inquiry.id?.slice(-8)}</h3>
                  <InquiryMeta>
                    <span>Intent: {inquiry.intent}</span>
                    <span>
                      Confidence: {(inquiry.confidence * 100).toFixed(1)}%
                    </span>
                    <span>
                      Source:{" "}
                      {inquiry.isDialogflowResponse ? "Dialogflow" : "Fallback"}
                    </span>
                    <span>Date: {formatDate(inquiry.timestamp)}</span>
                    {inquiry.userEmail && (
                      <UserEmail
                        onClick={() => copyEmailToClipboard(inquiry.userEmail!)}
                      >
                        📧 {inquiry.userEmail}
                        <CopyButton title="Copy email">
                          {copiedEmail === inquiry.userEmail ? "✓" : "📋"}
                        </CopyButton>
                      </UserEmail>
                    )}
                  </InquiryMeta>
                </div>
                <StatusBadge $status={inquiry.status}>
                  {inquiry.status}
                </StatusBadge>
              </InquiryHeader>

              <MessageContent>
                <div>
                  <strong>User Message:</strong>
                </div>
                <MessageText>{inquiry.message}</MessageText>

                <div>
                  <strong>AI Response:</strong>
                </div>
                <ResponseText>{inquiry.response}</ResponseText>

                {inquiry.adminNotes && (
                  <div>
                    <strong>Admin Notes:</strong>
                    <MessageText>{inquiry.adminNotes}</MessageText>
                  </div>
                )}

                {inquiry.adminResponse && (
                  <div>
                    <strong>Admin Response:</strong>
                    <ResponseText>{inquiry.adminResponse}</ResponseText>
                  </div>
                )}
              </MessageContent>

              <ActionButtons>
                <Button
                  $variant="primary"
                  onClick={() => handleStatusChange(inquiry.id!, "reviewed")}
                  disabled={inquiry.status === "reviewed"}
                >
                  Mark Reviewed
                </Button>

                <Button
                  $variant="secondary"
                  onClick={() => setEditingInquiry(inquiry.id!)}
                >
                  Add Notes
                </Button>

                <Button
                  $variant="primary"
                  onClick={() => handleStatusChange(inquiry.id!, "resolved")}
                  disabled={inquiry.status === "resolved"}
                >
                  Mark Resolved
                </Button>

                <Button
                  $variant="secondary"
                  onClick={() => handleStatusChange(inquiry.id!, "archived")}
                  disabled={inquiry.status === "archived"}
                >
                  Archive
                </Button>
              </ActionButtons>

              {editingInquiry === inquiry.id && (
                <div style={{ marginTop: "1rem" }}>
                  <div>
                    <strong>Add Admin Notes:</strong>
                  </div>
                  <AdminNotes
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add your notes here..."
                  />

                  <div style={{ marginTop: "0.5rem" }}>
                    <strong>Admin Response (optional):</strong>
                    <AdminNotes
                      value={adminResponse}
                      onChange={(e) => setAdminResponse(e.target.value)}
                      placeholder="Add a response to the user..."
                      style={{ minHeight: "60px" }}
                    />
                  </div>

                  <ActionButtons style={{ marginTop: "0.5rem" }}>
                    <Button
                      $variant="primary"
                      onClick={() => handleSaveNotes(inquiry.id!)}
                    >
                      Save Notes
                    </Button>

                    <Button
                      $variant="primary"
                      onClick={() => handleResolve(inquiry.id!)}
                    >
                      Resolve with Response
                    </Button>

                    <Button
                      $variant="secondary"
                      onClick={() =>
                        handleStatusChange(inquiry.id!, "archived")
                      }
                      disabled={inquiry.status === "archived"}
                    >
                      Archive
                    </Button>

                    <Button
                      $variant="secondary"
                      onClick={() => {
                        setEditingInquiry(null);
                        setAdminNotes("");
                        setAdminResponse("");
                      }}
                    >
                      Cancel
                    </Button>
                  </ActionButtons>
                </div>
              )}
            </InquiryCard>
          );
        })}
      </InquiriesList>
    </ManagementContainer>
  );
};

export default InquiryManagement;
